import { FC, useEffect } from 'react';
import { useTranslation } from 'next-i18next';
import Link from 'next/link';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { link } from '@phoenix/core/frontend/shared/util/navigation/link.utils';
import { Button } from '@phoenix/design-system/frontend/ui/button/button';
import { getDesignSystemIcon } from '@phoenix/design-system/frontend/util/path.utils';
import { PhoenixImage } from '@phoenix/core/frontend/shared/ui/assets/phoenix-image';
import { useSearchTools } from '@phoenix/team-m/my-tool-shed/frontend/shared/use-search-tools';
import { useNotificationContext } from '@phoenix/team-m/my-tool-shed/frontend/shared/use-notification-context';
import { containerQueries } from '@phoenix/design-system/frontend/ui/breakpoints/breakpoints';

import { ToolsNotFoundModal } from '../common/modal/tools-not-found-modal';

const plusIcon = getDesignSystemIcon('icon-plus-16.svg');
const magnifyingGlassIcon = getDesignSystemIcon('icon-magnifying-glass-16.svg');

export const EmptyToolShed: FC = () => {
  const { 'product-automatic-search': automaticSearchFlagEnabled } = useFlags();
  const { t } = useTranslation();
  const { createErrorNotification } = useNotificationContext();
  const { fetchSearchProducts, isSearching, showError } = useSearchTools();

  useEffect(() => {
    if (showError) {
      createErrorNotification({ id: 'add-searched-tool-error', message: t('addTool_entry_option_search_error_general') });
    }
  }, [showError]);

  return (
    <div className="mt-12 flex flex-col items-center desktop:mt-20" data-testid="toolShedEmpty">
      <div className="relative aspect-video h-full w-full max-w-xl">
        <PhoenixImage
          src="empty_state_composition_transparent.png"
          layout="fill"
          sizes={`(max-width: ${containerQueries.tablet}) 100vw, 50vw`}
          staticTags={['imagesMyToolShed']}
        />
      </div>
      <div className="max-w-xl">
        <h2 className="mt-8 text-center stihl-text-h4-mobile">{t('toolShed_overview_empty_headline')}</h2>
        <p className="mt-3 text-center stihl-text-body-default-reg">{t('toolShed_overview_empty_copyText')}</p>
      </div>
      {!automaticSearchFlagEnabled && (
        <>
          <div className="mt-8 flex tablet:space-x-6 mobileOnly:w-full mobileOnly:flex-col mobileOnly:space-y-4 miniTabletOnly:w-full miniTabletOnly:flex-col miniTabletOnly:space-y-4">
            <Link href={link('/tool-shed/add-tool')} legacyBehavior>
              <Button
                className="mobileOnly:w-full miniTabletOnly:w-full"
                btnType="white_default"
                icon={plusIcon}
                iconPosition="left"
                data-testid="addToolBtn"
              >
                {t('toolShed_overview_button_addTool')}
              </Button>
            </Link>
            <Button
              className="mobileOnly:w-full miniTabletOnly:w-full"
              btnType="white_outline"
              icon={magnifyingGlassIcon}
              iconPosition="left"
              loader={isSearching}
              onClick={fetchSearchProducts}
              data-testid="findToolsBtn"
            >
              {t('addTool_entry_option_search_button_findTool')}
            </Button>
          </div>
          <ToolsNotFoundModal />
        </>
      )}
    </div>
  );
};
